import { useCallback, useEffect, useState } from "react";
import type { ClarificationItemShape, ClarificationAnswer, ItemState } from "./types";
import { initItemState, collectAnswer, isItemBlocking } from "./types";
import { ClarificationQuestion } from "./ClarificationQuestion";

interface ClarificationPanelProps {
    items: ClarificationItemShape[];
    onConfirm: (answers: ClarificationAnswer[]) => void;
    onFilterReject?: () => void;
    /** Render prop: given an item, returns a preview card node (or null) */
    renderPreviewCard?: (item: ClarificationItemShape) => React.ReactNode;
    /** Render prop: given an item, returns the styled message body node */
    renderMessageBody: (item: ClarificationItemShape) => React.ReactNode;
}

export function ClarificationPanel({
    items,
    onConfirm,
    onFilterReject,
    renderPreviewCard,
    renderMessageBody,
}: ClarificationPanelProps) {
    const [states, setStates] = useState<ItemState[]>(() => items.map(initItemState));

    useEffect(() => {
        setStates(items.map(initItemState));
    }, [items]);

    const updateState = useCallback((idx: number, next: ItemState) => {
        setStates((prev) => {
            const updated = [...prev];
            updated[idx] = next;
            return updated;
        });
    }, []);

    const isDisabled = items.some((item, idx) => isItemBlocking(item, states[idx]));

    const hasFilterErrors = items.some(
        (item, idx) =>
            !!item.filterPayload && (states[idx]?.filterDrafts ?? []).some((f) => !!f.error),
    );

    const handleConfirm = () => {
        onConfirm(items.map((item, idx) => collectAnswer(item, states[idx] ?? initItemState(item))));
    };

    return (
        <section className="rounded-xl border border-[#079a95]/30 bg-[#079a95]/5 px-4 py-3 space-y-4">
            <span className="block text-xs font-semibold uppercase tracking-wide text-[#079a95]">
                A few questions
            </span>

            {items.map((item, idx) => (
                <ClarificationQuestion
                    key={item.title}
                    item={item}
                    idx={idx}
                    state={states[idx] ?? initItemState(item)}
                    onChange={(s) => updateState(idx, s)}
                    messageBody={renderMessageBody(item)}
                    previewCard={renderPreviewCard?.(item) ?? undefined}
                />
            ))}

            <div className="flex gap-2">
                {hasFilterErrors && (
                    <button
                        type="button"
                        onClick={onFilterReject}
                        className="flex-1 cursor-pointer rounded-lg border border-[#d14343] bg-white px-4 py-2 text-sm font-medium text-[#d14343] transition-colors hover:bg-[#d14343]/5"
                    >
                        Reject &amp; refine prompt
                    </button>
                )}
                <button
                    type="button"
                    disabled={isDisabled}
                    onClick={handleConfirm}
                    className="flex-1 cursor-pointer rounded-lg bg-[#079a95] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#067a76] disabled:cursor-not-allowed disabled:opacity-40"
                >
                    Continue
                </button>
            </div>
        </section>
    );
}
